import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Modal,
  Form,
  Input,
  Upload,
  Button,
  Select,
  Row,
  Col,
  Space,
  Tabs,
  message,
  Typography,
  Checkbox
} from 'antd';
import { FileTextOutlined, EditOutlined, PlusOutlined, UploadOutlined, RightOutlined, LeftOutlined } from '@ant-design/icons';
import SlateEditor from '../SlateEditor/SlateEditor';
import apiClient from '../../services/api';
import './NewNoteModal.css';

const { TabPane } = Tabs;
const { Option } = Select;
const { Text } = Typography;
const { TextArea } = Input;

/**
 * 将 Tiptap getMarkdown() 的输出恢复为标准 Markdown 形式（便于解析与后续使用）。
 * Tiptap 序列化会把方括号等转义为 \[ \]，此处统一还原。
 * @param {string} content - 从 Tiptap 读出的 markdown 字符串
 * @returns {string} 标准 markdown 字符串
 */
function restoreTiptapMarkdownToStandard(content) {
  if (content == null || typeof content !== 'string') return content || '';
  return content
    .replace(/\\\]/g, ']')
    .replace(/\\\[/g, '[');
}

/**
 * 将 API 返回的 content 中字面量 \\n / \\t 转为真实换行与制表符，供 SlateEditor 正确解析 Markdown。
 * 支持单次转义 (\\n) 与双重转义 (\\\\n)。
 * @param {string} str - 原始字符串
 * @returns {string}
 */
function normalizeMarkdownFromApi(str) {
  if (str == null || typeof str !== 'string') return str || '';
  let s = str;
  // 先处理双重转义，再处理单次转义
  s = s.replace(/\\\\n/g, '\n').replace(/\\\\t/g, '\t');
  s = s.replace(/\\n/g, '\n').replace(/\\t/g, '\t');
  return s;
}

/**
 * New Note 模态框：支持 Link/File 上传与 Manual Input 两种创建方式
 * @param {boolean} [enableAIWriter=false] - 是否在打开时弹出 AI Writer 助手弹窗，缺省为 false
 */
const NewNoteModal = ({
  open,
  onCancel,
  batchResult,
  selectedFiles,
  setSelectedFiles,
  createNoteKnowledgeBase,
  createAvailableKnowledgeBases,
  handleCreateNoteKnowledgeBaseChange,
  linkForm,
  handleCreateFromLink,
  createForm,
  manualInputContent,
  setManualInputContent,
  handleCreateNote,
  isBatchCreating,
  createNoteProtection,
  setIsCreateModalVisible,
  setIsBatchCreating,
  setIsCreatingNote,
  handleCreateModalCancel,
  enableAIWriter = false
}) => {
  const { t } = useTranslation(['dashboard', 'common', 'messages']);
  const [aiGenInput, setAiGenInput] = useState('');
  /** confirm 页：目标需求（纯文本，多行输入） */
  const [confirmRequirementText, setConfirmRequirementText] = useState('');
  /** confirm 页：目录建议（Markdown，Tiptap 编辑） */
  const [confirmChapterIndexContent, setConfirmChapterIndexContent] = useState('');
  const [generateIndexLoading, setGenerateIndexLoading] = useState(false);
  const [generateContentLoading, setGenerateContentLoading] = useState(false);
  const [generatedIndex, setGeneratedIndex] = useState(null);
  /** 仅在外部分入内容（如 AI 生成）时递增，用于让 Content SlateEditor 重新挂载以显示新内容 */
  const [contentVersion, setContentVersion] = useState(0);
  /** AI Writer 独立弹窗：enableAIWriter 为 true 时打开 New Note 即弹出 */
  const [aiWriterModalOpen, setAiWriterModalOpen] = useState(false);
  /** AI Writer 弹窗内步骤：input=输入需求+Submit，confirm=显示目录确认+回退/撰写 */
  const [aiWriterStep, setAiWriterStep] = useState('input');
  const [aiWriterMaxChapters, setAiWriterMaxChapters] = useState(3);
  const [aiWriterEnableOnlineSource, setAiWriterEnableOnlineSource] = useState(true);
  /** confirm 页：内容长度（字数上限） */
  const [aiWriterContentLength, setAiWriterContentLength] = useState(3000);

  useEffect(() => {
    if (open && enableAIWriter === true) {
      setAiWriterModalOpen(true);
      setAiWriterStep('input');
      setAiGenInput('');
    } else {
      setAiWriterModalOpen(false);
      setAiWriterStep('input');
    }
  }, [open, enableAIWriter]);

  /** 打开 modal 时：所有输入/编辑框置空，选择组件恢复缺省 */
  useEffect(() => {
    if (!open) return;
    createForm?.setFieldsValue?.({ title: '', content: '', knowledge_base: undefined });
    setManualInputContent?.('');
    setContentVersion((v) => v + 1);
    linkForm?.setFieldsValue?.({ title: '', url: '' });
    setSelectedFiles?.([]);
    handleCreateNoteKnowledgeBaseChange?.(undefined);
    setAiGenInput('');
    setConfirmRequirementText('');
    setConfirmChapterIndexContent('');
    setGeneratedIndex(null);
    setAiWriterStep('input');
    setAiWriterMaxChapters(3);
    setAiWriterEnableOnlineSource(true);
    setAiWriterContentLength(3000);
  }, [open]);

  const handleClose = () => {
    setIsCreateModalVisible(false);
    setIsBatchCreating(false);
    setIsCreatingNote(false);
    createNoteProtection.release();
    handleCreateModalCancel();
  };

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      footer={null}
      width="70%"
      style={{ top: 20 }}
      maskClosable={true}
      closable={true}
      title={
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <FileTextOutlined style={{ marginRight: 8, color: '#667eea' }} />
            <span style={{ fontSize: '13px' }}>{t('dashboard:sidebar.new_note', { defaultValue: 'New Note' })}（aiwriter={enableAIWriter === true ? 'true' : 'false'}）</span>
          </div>
          {batchResult && (
            <div style={{ fontSize: '12px', color: '#52c41a', fontWeight: 'bold' }}>
              ✅ {batchResult.totalCount > 1 ? t('dashboard:batch_create_done', { defaultValue: 'Batch created!' }) : t('dashboard:note_create_done', { defaultValue: 'Note created!' })}
            </div>
          )}
        </div>
      }
      styles={{
        body: { padding: '24px' },
        mask: { backgroundColor: 'rgba(0, 0, 0, 0.45)' }
      }}
    >
      {/* 批量创建结果显示 */}
      {batchResult && (
        <div style={{
          marginBottom: 16,
          padding: 16,
          backgroundColor: '#f6ffed',
          border: '1px solid #b7eb8f',
          borderRadius: 6,
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#52c41a', marginBottom: 8 }}>
            🎉 {batchResult.totalCount > 1 ? t('dashboard:batch_create_done', { defaultValue: 'Batch created!' }) : t('dashboard:note_create_done', { defaultValue: 'Note created!' })}
          </div>
          <div style={{ fontSize: '14px', color: '#666' }}>
            {t('dashboard:created_count', { defaultValue: 'Successfully created ' })}
            <span style={{ color: '#52c41a', fontWeight: 'bold' }}>{batchResult.successCount}</span>
            {t('dashboard:notes_noun', { defaultValue: ' notes' })}
            {batchResult.failedCount > 0 && (
              <span style={{ color: '#ff4d4f' }}>
                {t('dashboard:failed_files_count', { defaultValue: ', {{count}} files failed', count: batchResult.failedCount })}
              </span>
            )}
          </div>
          {batchResult.failedFiles.length > 0 && (
            <div style={{ marginTop: 8, fontSize: '12px', color: '#ff4d4f' }}>
              {t('dashboard:failed_files', { defaultValue: 'Failed files: ' })}{batchResult.failedFiles.map(f => f.fileName).join(', ')}
            </div>
          )}
        </div>
      )}

      <Tabs
        defaultActiveKey={enableAIWriter === true ? 'manual' : 'link'}
        activeKey={enableAIWriter === true ? 'manual' : undefined}
        centered
        tabBarStyle={{
          marginBottom: 24,
          ...(enableAIWriter === true ? { display: 'none' } : {})
        }}
      >
        {enableAIWriter !== true && (
        <TabPane
          tab={
            <span style={{ minWidth: '140px', display: 'inline-block', textAlign: 'center' }}>
              <FileTextOutlined style={{ marginRight: 4 }} />
              {t('dashboard:tab.link_or_file', { defaultValue: 'Link/File' })}
            </span>
          }
          key="link"
        >
          <Form
            form={linkForm}
            layout="vertical"
            onFinish={async (values) => {
              if (createNoteProtection.protect()) {
                try {
                  await handleCreateFromLink(values);
                } finally {}
              }
            }}
            initialValues={{ title: '', url: '' }}
          >
            <div style={{ marginBottom: 16 }}>
              <Text type="secondary">{t('dashboard:upload.choose_files', { defaultValue: 'Choose files to upload (batch):' })}</Text>
              <Upload
                beforeUpload={(file, fileList) => {
                  if (fileList.length + selectedFiles.length > 5) {
                    message.error(t('messages:max_files_limit', { defaultValue: 'You can select up to 5 files' }));
                    return Upload.LIST_IGNORE;
                  }
                  const allowedExtensions = ['.txt', '.md', '.pdf', '.pptx', '.xlsx', '.docx'];
                  const fileName = file.name.toLowerCase();
                  const fileExtension = fileName.substring(fileName.lastIndexOf('.'));

                  if (!allowedExtensions.includes(fileExtension)) {
                    const errorMsg = t('messages:file_type_not_supported', {
                      defaultValue: 'File type not supported: {{fileName}}. Allowed types: {{types}}',
                      fileName: file.name,
                      types: allowedExtensions.join(', ')
                    });
                    message.error(errorMsg);
                    return Upload.LIST_IGNORE;
                  }

                  const maxFileSize = 15 * 1024 * 1024;
                  if (file.size > maxFileSize) {
                    const fileSizeMB = (file.size / 1024 / 1024).toFixed(2);
                    const errorMsg = t('messages:file_too_large', {
                      error: `${file.name} (${fileSizeMB}MB): ${t('messages:file_size_exceeds_limit')}`
                    });
                    alert(errorMsg);
                    return Upload.LIST_IGNORE;
                  }

                  setSelectedFiles(prev => {
                    if (prev.length >= 5) return prev;
                    return [...prev, file];
                  });
                  return false;
                }}
                fileList={selectedFiles}
                onRemove={file => {
                  setSelectedFiles(prev => prev.filter(f => f.uid !== file.uid));
                }}
                multiple
                showUploadList
                accept=".txt,.md,.pdf,.pptx,.xlsx,.docx"
                style={{ marginTop: 8 }}
                disabled={isBatchCreating}
              >
                <Button
                  icon={<UploadOutlined />}
                  block
                  style={{ marginTop: 8 }}
                  disabled={isBatchCreating}
                  onClick={(e) => {
                    const buttonElement = e?.currentTarget || null;
                    if (!buttonElement) return;
                    try {
                      if (buttonElement && typeof buttonElement.closest === 'function') {
                        const uploadWrapper = buttonElement.closest('.ant-upload');
                        if (uploadWrapper && typeof uploadWrapper.querySelector === 'function') {
                          const fileInput = uploadWrapper.querySelector('input[type="file"]');
                          if (fileInput && fileInput instanceof HTMLInputElement) {
                            e.preventDefault();
                            e.stopPropagation();
                            fileInput.click();
                            return;
                          }
                        }
                        const modalBody = document.querySelector('.ant-modal-body');
                        if (modalBody && typeof modalBody.querySelector === 'function') {
                          const fileInput = modalBody.querySelector('input[type="file"]');
                          if (fileInput && fileInput instanceof HTMLInputElement) {
                            e.preventDefault();
                            e.stopPropagation();
                            fileInput.click();
                            return;
                          }
                        }
                      }
                    } catch (err) {
                      console.warn('[Upload] 查找文件输入元素失败:', err);
                    }
                  }}
                >
                  {t('dashboard:upload.select_files', { defaultValue: 'Select files (up to 5)' })}
                </Button>
              </Upload>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                {t('dashboard:upload.supported_formats', { defaultValue: 'Supports TXT, MD, PDF, DOC, DOCX. Each file creates a note.' })}
              </Text>
            </div>

            <Row align="middle" gutter={0} style={{ marginTop: 16, width: '100%' }}>
              <Col flex="80px">
                <Text strong style={{ marginRight: '0px' }}>{t('dashboard:form.kb', { defaultValue: 'Knowledge Base' })}</Text>
              </Col>
              <Col flex="auto">
                <Select
                  style={{ width: '100%' }}
                  placeholder={t('dashboard:form.please_select_kb', { defaultValue: 'Please select knowledge base' })}
                  value={createNoteKnowledgeBase?.kb_id}
                  onChange={handleCreateNoteKnowledgeBaseChange}
                  allowClear
                  disabled={isBatchCreating}
                >
                  {createAvailableKnowledgeBases.map(kb => (
                    <Option key={kb.kb_id} value={kb.kb_id}>
                      {kb.is_private ? t('dashboard:private_kb_short', { defaultValue: 'Personal' }) : `${t('dashboard:collaborative_kb_short', { defaultValue: 'Team' })} - ${kb.name}`}
                    </Option>
                  ))}
                </Select>
                {createAvailableKnowledgeBases.length === 0 && (
                  <div style={{ marginTop: 8, color: '#ff4d4f', fontSize: '12px' }}>
                    {t('dashboard:form.no_kb_found', { defaultValue: 'No linked knowledge bases found. Please contact support.' })}
                  </div>
                )}
              </Col>
            </Row>

            <Form.Item style={{ marginTop: 24, marginBottom: 0, textAlign: 'right' }}>
              <Space>
                <Button onClick={handleClose} disabled={isBatchCreating}>{t('common:actions.cancel', { defaultValue: 'Cancel' })}</Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={isBatchCreating ? null : <PlusOutlined />}
                  className="gradient-button"
                  disabled={createAvailableKnowledgeBases.length === 0 || selectedFiles.length === 0 || isBatchCreating || createNoteProtection.isProtected}
                  loading={isBatchCreating || createNoteProtection.isProtected}
                >
                  {isBatchCreating || createNoteProtection.isProtected
                    ? t('dashboard:batch_creating_with_count', { defaultValue: 'Batch creating... ({{count}} files)', count: selectedFiles.length })
                    : selectedFiles.length > 1
                      ? t('dashboard:batch_create_notes_with_count', { defaultValue: 'Batch create notes ({{count}} files)', count: selectedFiles.length })
                      : t('dashboard:create_note', { defaultValue: 'CREATE NOTE' })
                  }
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </TabPane>
        )}
        <TabPane
          tab={
            <span style={{ minWidth: '140px', display: 'inline-block', textAlign: 'center' }}>
              <EditOutlined style={{ marginRight: 4 }} />
              {t('dashboard:tab.manual_input', { defaultValue: 'Manual Input' })}
            </span>
          }
          key="manual"
        >
          <Form
            form={createForm}
            layout="vertical"
          >
            <Row align="middle" gutter={0} style={{ width: '100%' }}>
              <Col span={4}>
                <Text strong style={{ display: 'inline-block', width: '80px', textAlign: 'left' }}>
                  {t('dashboard:form.title', { defaultValue: 'Title' })}
                  <span style={{ color: '#ff4d4f', marginLeft: '4px' }}>*</span>
                </Text>
              </Col>
              <Col span={20}>
                <Form.Item
                  name="title"
                  style={{ marginBottom: 0 }}
                >
                  <Input placeholder={t('dashboard:form.enter_card_title', { defaultValue: 'Enter card title' })} size="large" disabled={isBatchCreating} />
                </Form.Item>
              </Col>
            </Row>

            <Row align="top" gutter={0} style={{ marginTop: 16, width: '100%' }}>
              <Col span={4}>
                <Text strong style={{ display: 'inline-block', width: '80px', textAlign: 'left', lineHeight: '32px' }}>
                  {t('dashboard:form.content', { defaultValue: 'Content' })}
                  <span style={{ color: '#ff4d4f', marginLeft: '4px' }}>*</span>
                </Text>
              </Col>
              <Col span={20}>
                <Form.Item
                  style={{ marginBottom: 8 }}
                >
                  <SlateEditor
                    key={contentVersion}
                    value={manualInputContent}
                    initialMarkdown={normalizeMarkdownFromApi(manualInputContent)}
                    onChange={(content) => {
                      setManualInputContent(content);
                      createForm.setFieldsValue({ content });
                    }}
                    placeholder={t('dashboard:form.enter_note_content', { defaultValue: 'Enter note content' })}
                    height={300}
                  />
                </Form.Item>
                <Text type="secondary" style={{ fontSize: '12px', fontStyle: 'italic', textAlign: 'left', display: 'block' }}>
                  💡 {t('dashboard:form.auto_summary_tip', { defaultValue: 'An AI summary will be generated automatically.' })}
                </Text>
              </Col>
            </Row>

            <Row align="middle" gutter={0} style={{ marginTop: 16, width: '100%' }}>
              <Col span={4}>
                <Text strong style={{ display: 'inline-block', width: '80px', textAlign: 'left' }}>
                  {t('dashboard:form.kb', { defaultValue: 'Knowledge Base' })}
                  <span style={{ color: '#ff4d4f', marginLeft: '4px' }}>*</span>
                </Text>
              </Col>
              <Col span={20}>
                <Form.Item
                  name="knowledge_base"
                  style={{ marginBottom: 0 }}
                >
                  <Select
                    style={{ width: '100%' }}
                    placeholder={t('dashboard:form.please_select_kb', { defaultValue: 'Please select knowledge base' })}
                    value={createNoteKnowledgeBase?.kb_id}
                    onChange={(value) => {
                      handleCreateNoteKnowledgeBaseChange(value);
                      createForm.setFieldsValue({ knowledge_base: value });
                    }}
                    allowClear
                    disabled={isBatchCreating}
                  >
                    {createAvailableKnowledgeBases.map(kb => (
                      <Option key={kb.kb_id} value={kb.kb_id}>
                        {kb.is_private ? t('dashboard:private_kb', { defaultValue: 'Personal' }) : `${t('dashboard:collaborative_kb', { defaultValue: 'Team' })} - ${kb.name}`}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
                {createAvailableKnowledgeBases.length === 0 && (
                  <div style={{ marginTop: 8, color: '#ff4d4f', fontSize: '12px' }}>
                    {t('dashboard:form.no_kb_found', { defaultValue: 'No linked knowledge bases found. Please contact support.' })}
                  </div>
                )}
              </Col>
            </Row>

            <Form.Item style={{ marginTop: 24, textAlign: 'right' }}>
              <Space>
                <Button onClick={handleClose} disabled={isBatchCreating}>{t('common:actions.cancel', { defaultValue: 'Cancel' })}</Button>
                <Button
                  type="primary"
                  icon={isBatchCreating ? null : <PlusOutlined />}
                  className="gradient-button"
                  disabled={createAvailableKnowledgeBases.length === 0 || isBatchCreating || createNoteProtection.isProtected}
                  loading={isBatchCreating || createNoteProtection.isProtected}
                  onClick={async () => {
                    const values = createForm.getFieldsValue();
                    const content = manualInputContent || values.content;
                    if (!values.title || !values.title.trim()) {
                      message.error(t('messages:please_enter_title', { defaultValue: 'Please enter title' }));
                      return;
                    }
                    if (!content || !content.trim()) {
                      message.error(t('messages:please_enter_content', { defaultValue: 'Please enter content' }));
                      return;
                    }
                    if (!values.knowledge_base) {
                      message.error(t('messages:please_select_kb', { defaultValue: 'Please select knowledge base' }));
                      return;
                    }
                    values.content = content;
                    if (createNoteProtection.protect()) {
                      try {
                        if (handleCreateNote) {
                          await handleCreateNote(values);
                        }
                      } finally {}
                    }
                  }}
                >
                  {isBatchCreating || createNoteProtection.isProtected
                    ? t('dashboard:creating', { defaultValue: 'Creating...' })
                    : t('dashboard:create_note', { defaultValue: 'Create Note' })
                  }
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </TabPane>
      </Tabs>

      {/* AI Writer Assistant 独立弹窗：enableAIWriter 为 true 时打开 New Note 即弹出 */}
      {enableAIWriter === true && (
        <Modal
          open={aiWriterModalOpen}
          onCancel={() => {
            setAiWriterModalOpen(false);
            setAiWriterStep('input');
            // 先关闭本弹窗（state 生效），下一 tick 再关 New Note，避免父组件卸载导致本弹窗未关闭
            setTimeout(() => handleClose(), 0);
          }}
          footer={null}
          width={aiWriterStep === 'confirm' ? 676 : 560}
          title={
            <span style={{ fontSize: 14 }}>
              {t('dashboard:form.ai_writer_assistant', { defaultValue: 'AI Doc Writer' })}
              {aiWriterStep === 'confirm' && (
                <span style={{ fontWeight: 'normal', color: '#888', marginLeft: 8 }}>
                  — {t('dashboard:form.confirm_outline', { defaultValue: 'Confirm outline' })}
                </span>
              )}
            </span>
          }
          destroyOnClose={false}
          styles={{ body: { padding: '20px 24px' } }}
        >
          {aiWriterStep === 'input' && (
            <>
              <div style={{ marginBottom: 6, fontWeight: 500 }}>
                {t('dashboard:form.ai_writer_section_requirement', { defaultValue: 'Writing Brief' })}
              </div>
              <TextArea
                value={aiGenInput}
                onChange={(e) => setAiGenInput(e.target.value)}
                placeholder={t('dashboard:form.ai_writer_placeholder', { defaultValue: 'Enter content requirements and any rough materials for the new note' })}
                autoSize={{ minRows: 4, maxRows: 8 }}
                style={{
                  marginBottom: 12,
                  backgroundColor: '#fafafa',
                  borderRadius: 6
                }}
              />
              <div className="ai-writer-options-row" style={{ marginBottom: 16 }}>
                <Row gutter={24} align="middle" style={{ width: '100%' }}>
                  <Col>
                    <Space align="center" size={8}>
                      <Text type="secondary" style={{ margin: 0 }}>
                        {t('dashboard:form.ai_writer_max_chapters', { defaultValue: 'Max chapters' })}
                      </Text>
                      <Select
                        value={aiWriterMaxChapters}
                        onChange={setAiWriterMaxChapters}
                        className="ai-writer-max-chapters-select"
                        style={{ minWidth: 100 }}
                        size="small"
                        dropdownStyle={{ fontSize: '0.7rem' }}
                        options={Array.from({ length: 10 }, (_, i) => ({ value: i + 1, label: String(i + 1) }))}
                      />
                    </Space>
                  </Col>
                  <Col>
                    <Space align="center" size={8}>
                      <Text type="secondary" style={{ margin: 0 }}>
                        {t('dashboard:form.ai_writer_enable_online_source', { defaultValue: 'Enable Online Source' })}
                      </Text>
                      <Checkbox
                        checked={aiWriterEnableOnlineSource}
                        onChange={(e) => setAiWriterEnableOnlineSource(e.target.checked)}
                        className="ai-writer-options-row-checkbox"
                      />
                    </Space>
                  </Col>
                </Row>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  type="primary"
                  icon={<RightOutlined />}
                  loading={generateIndexLoading}
                  disabled={generateIndexLoading}
                  onClick={async () => {
                    const requirementStr = (aiGenInput || '').trim();
                    if (!requirementStr) {
                      message.warning(t('dashboard:form.ai_writer_placeholder', { defaultValue: 'Please enter content requirements' }));
                      return;
                    }
                    setConfirmRequirementText(requirementStr);
                    setConfirmChapterIndexContent(t('messages:loading', { defaultValue: 'Loading...' }));
                    setGenerateIndexLoading(true);
                    try {
                      const { data } = await apiClient.post('/notes/aiwriter/generate-index', {
                        requirementStr,
                        maxChapters: aiWriterMaxChapters,
                        enableOnlineSource: aiWriterEnableOnlineSource
                      });
                      setGeneratedIndex(data);
                      const indexContent = (data && (data.index != null)) ? String(data.index) : '';
                      setConfirmChapterIndexContent(indexContent);
                      message.success(
                        data?.chapters?.length
                          ? t('dashboard:form.index_generated_chapters', { count: data.chapters.length, defaultValue: `Generated {{count}} chapters` })
                          : t('dashboard:form.index_generated', { defaultValue: 'Index generated' })
                      );
                      setAiWriterStep('confirm');
                    } catch (err) {
                      const msg = err.response?.data?.message || err.message || t('messages:generate_index_failed', { defaultValue: 'Failed to generate index' });
                      message.error(msg);
                    } finally {
                      setGenerateIndexLoading(false);
                    }
                  }}
                >
                  {t('common:actions.next', { defaultValue: 'Next' })}
                </Button>
              </div>
            </>
          )}
          {aiWriterStep === 'confirm' && (
            <>
              <div style={{ marginBottom: 16 }}>
                <div style={{ marginBottom: 6, fontWeight: 500 }}>
                  {t('dashboard:form.ai_writer_section_requirement', { defaultValue: 'Writing Brief' })}
                </div>
                <TextArea
                  value={confirmRequirementText}
                  onChange={(e) => setConfirmRequirementText(e.target.value)}
                  placeholder={t('dashboard:form.ai_writer_placeholder', { defaultValue: 'Enter content requirements' })}
                  autoSize={{ minRows: 3, maxRows: 8 }}
                  style={{ width: '100%', borderRadius: 6 }}
                  disabled={generateIndexLoading}
                />
              </div>
              <div style={{ marginBottom: 24 }}>
                <div style={{ marginBottom: 6, fontWeight: 500 }}>
                  {t('dashboard:form.ai_writer_section_index', { defaultValue: 'Recommended Outline' })}
                </div>
                {generateIndexLoading ? (
                  <div style={{ padding: 24, textAlign: 'center', backgroundColor: '#fafafa', border: '1px solid #d9d9d9', borderRadius: 6, color: '#666', minHeight: 200 }}>
                    {t('messages:loading', { defaultValue: 'Loading...' })}
                  </div>
                ) : (
                  <SlateEditor
                    key={generatedIndex ? 'index' : 'empty'}
                    value={confirmChapterIndexContent}
                    initialMarkdown={normalizeMarkdownFromApi(confirmChapterIndexContent)}
                    onChange={(content) => setConfirmChapterIndexContent(content)}
                    placeholder={t('dashboard:form.index_suggestion_placeholder', { defaultValue: 'Generated outline will appear here' })}
                    height={280}
                    style={{ width: '100%' }}
                  />
                )}
              </div>
              <div className="ai-writer-options-row" style={{ marginBottom: 24 }}>
                <Row gutter={24} align="middle" style={{ width: '100%' }}>
                  <Col>
                    <Space align="center" size={8}>
                      <Text type="secondary" style={{ margin: 0 }}>
                        {t('dashboard:form.ai_writer_content_length', { defaultValue: 'Content Length' })}
                      </Text>
                      <Select
                        value={aiWriterContentLength}
                        onChange={setAiWriterContentLength}
                        className="ai-writer-content-length-select"
                        style={{ minWidth: 100 }}
                        size="small"
                        dropdownStyle={{ fontSize: '0.7rem' }}
                        options={[3000, 2500, 2000, 1500, 1000, 500].map((n) => ({ value: n, label: String(n) }))}
                      />
                    </Space>
                  </Col>
                  <Col>
                    <Space align="center" size={8}>
                      <Text type="secondary" style={{ margin: 0 }}>
                        {t('dashboard:form.ai_writer_enable_online_source', { defaultValue: 'Enable online source' })}
                      </Text>
                      <Checkbox
                        checked={aiWriterEnableOnlineSource}
                        onChange={(e) => setAiWriterEnableOnlineSource(e.target.checked)}
                        className="ai-writer-options-row-checkbox"
                      />
                    </Space>
                  </Col>
                </Row>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginTop: 8 }}>
                <Button
                  icon={<LeftOutlined />}
                  onClick={() => setAiWriterStep('input')}
                  disabled={generateIndexLoading}
                >
                  {t('common:actions.back', { defaultValue: 'Back' })}
                </Button>
                <Space>
                  <Button
                    onClick={() => {
                      setAiWriterModalOpen(false);
                      setAiWriterStep('input');
                      setGeneratedIndex(null);
                      setConfirmRequirementText('');
                      setConfirmChapterIndexContent('');
                    }}
                    disabled={generateContentLoading}
                  >
                    {t('common:actions.close', { defaultValue: 'Close' })}
                  </Button>
                  {generatedIndex ? (
                    <Button
                      type="primary"
                      loading={generateContentLoading}
                      onClick={async () => {
                        const requirementStr = (confirmRequirementText || '').trim();
                        const chapterIndexStr = restoreTiptapMarkdownToStandard(confirmChapterIndexContent || '').trim();
                        if (!requirementStr) {
                          message.warning(t('dashboard:form.ai_writer_placeholder', { defaultValue: 'Please enter content requirements' }));
                          return;
                        }
                        if (!chapterIndexStr) {
                          message.warning(t('dashboard:form.ai_writer_parse_tip', { defaultValue: 'Please ensure [Index Suggestion] has content.' }));
                          return;
                        }
                        setGenerateContentLoading(true);
                        try {
                          const { data } = await apiClient.post('/notes/aiwriter/generate-content', {
                            requirementStr,
                            chapterIndexStr,
                            maxContentLength: aiWriterContentLength,
                            enableOnlineSource: aiWriterEnableOnlineSource
                          });
                          const title = (data && data.title != null) ? String(data.title) : '';
                          const rawContent = (data && data.content != null) ? String(data.content) : '';
                          const content = normalizeMarkdownFromApi(rawContent);
                          createForm.setFieldsValue({ title });
                          setManualInputContent(content);
                          createForm.setFieldsValue({ content });
                          setContentVersion((v) => v + 1);
                          setAiWriterModalOpen(false);
                          setAiWriterStep('input');
                          setGeneratedIndex(null);
                          setConfirmRequirementText('');
                          setConfirmChapterIndexContent('');
                          message.success(t('dashboard:form.write_chapter_success', { defaultValue: 'Chapter content has been filled into title and content.' }));
                        } catch (err) {
                          const msg = err.response?.data?.message || err.message || t('messages:generate_content_failed', { defaultValue: 'Failed to generate chapter content' });
                          message.error(msg);
                        } finally {
                          setGenerateContentLoading(false);
                        }
                      }}
                    >
                      {t('common:actions.confirm', { defaultValue: 'Confirm' })}
                    </Button>
                  ) : (
                    <Button
                      type="primary"
                      loading={generateContentLoading}
                      onClick={async () => {
                        const requirementStr = (confirmRequirementText || '').trim();
                        const chapterIndexStr = restoreTiptapMarkdownToStandard(confirmChapterIndexContent || '').trim();
                        if (!requirementStr) {
                          message.warning(t('dashboard:form.ai_writer_placeholder', { defaultValue: 'Please enter content requirements' }));
                          return;
                        }
                        if (!chapterIndexStr) {
                          message.warning(t('dashboard:form.ai_writer_parse_tip', { defaultValue: 'Please ensure [Index Suggestion] has content.' }));
                          return;
                        }
                        setGenerateContentLoading(true);
                        try {
                          const { data } = await apiClient.post('/notes/aiwriter/generate-content', {
                            requirementStr,
                            chapterIndexStr,
                            maxContentLength: aiWriterContentLength,
                            enableOnlineSource: aiWriterEnableOnlineSource
                          });
                          const title = (data && data.title != null) ? String(data.title) : '';
                          const rawContent = (data && data.content != null) ? String(data.content) : '';
                          const content = normalizeMarkdownFromApi(rawContent);
                          createForm.setFieldsValue({ title });
                          setManualInputContent(content);
                          createForm.setFieldsValue({ content });
                          setContentVersion((v) => v + 1);
                          setAiWriterModalOpen(false);
                          setAiWriterStep('input');
                          setGeneratedIndex(null);
                          setConfirmRequirementText('');
                          setConfirmChapterIndexContent('');
                          message.success(t('dashboard:form.write_chapter_success', { defaultValue: 'Chapter content has been filled into title and content.' }));
                        } catch (err) {
                          const msg = err.response?.data?.message || err.message || t('messages:generate_content_failed', { defaultValue: 'Failed to generate chapter content' });
                          message.error(msg);
                        } finally {
                          setGenerateContentLoading(false);
                        }
                      }}
                    >
                      {t('dashboard:form.write', { defaultValue: 'Write' })}
                    </Button>
                  )}
                </Space>
              </div>
            </>
          )}
        </Modal>
      )}

    </Modal>
  );
};

export default NewNoteModal;
